// Geteilte Bausteine & Helfer des Dashboards (Refined-Stil): Karten-Hülle mit
// Serifen-Überschrift + viel Schatten, Segmented-Control, Filter-Pill,
// Initialen-Avatar, Status-Chip sowie Datums-/Zeit-Helfer.
import * as React from "react";
import { Avatar } from "@fluentui/react-components";
import { CalendarLtr16Regular } from "@fluentui/react-icons";
import { REFINED, BRAND } from "../risikoanalyseStep/tokens";
import { useResponsive } from "../../hooks/useResponsive";
import { usePersistentCollapsed } from "../../hooks/usePersistentCollapsed";
import { CollapseToggle } from "../common/CollapseToggle";
import { toGraphDate } from "../../utils/graphDatum";

/* ────────── Datums-/Zeit-Helfer ────────── */

/** Graph liefert UTC ohne 'Z' — anhängen, sonst interpretiert der Browser lokal. */
// Wohnt seit 14.09.2026 in `utils/graphDatum.ts` (reine Funktion, auch ohne
// React-Baum nutzbar); hier importiert UND re-exportiert, damit die Datei sie
// selbst nutzen kann und alle bisherigen Importpfade gelten.
export { toGraphDate };

export const startOfDay = (d: Date): Date => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};

/** Datum-Spalten (frist/twv) kommen als "YYYY-MM-DD" oder ISO — robust auf den lokalen Tag bringen. */
const dayDate = (iso?: string | null): Date | null => {
    if (!iso) return null;
    // Reines Datum (twv/frist: "YYYY-MM-DD", ohne Zeit) → als lokaler Kalendertag.
    const pure = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (pure) return new Date(Number(pure[1]), Number(pure[2]) - 1, Number(pure[3]));
    // Datum MIT Zeit (Graph-Event liefert UTC OHNE 'Z') → korrekt zonen-parsen
    // (toGraphDate hängt 'Z' an), dann auf den LOKALEN Tagesbeginn bringen. Sonst
    // würde das UTC-Datums-Präfix genutzt → Abend-/Nacht-Termine fielen aus „Heute".
    const d = toGraphDate(iso);
    return Number.isNaN(d.getTime()) ? null : startOfDay(d);
};

export const isToday = (iso?: string | null): boolean => {
    const d = dayDate(iso);
    if (!d) return false;
    return d.getTime() === startOfDay(new Date()).getTime();
};

export const isFutureDay = (iso?: string | null): boolean => {
    const d = dayDate(iso);
    if (!d) return false;
    return d.getTime() > startOfDay(new Date()).getTime();
};

export const isPastDay = (iso?: string | null): boolean => {
    const d = dayDate(iso);
    if (!d) return false;
    return d.getTime() < startOfDay(new Date()).getTime();
};

/* ────────── Cluster-Prädikate (Heute/Woche · fällig=Frist / geplant=TWV) ──────────
 * Bauen auf startOfDay/isToday/isPastDay/isFutureDay auf — KEINE neuen Datums-
 * Primitiven. „fällig" = Frist, „geplant" = TWV. Wochengrenze Mo–So (ISO). */

/** Montag (00:00) der Woche, in der `d` liegt — ISO (Woche beginnt Montag). */
const startOfIsoWeek = (d: Date): Date => {
    const x = startOfDay(d);
    const dow = x.getDay(); // 0=So … 6=Sa
    const diff = dow === 0 ? -6 : 1 - dow; // auf Montag zurück
    x.setDate(x.getDate() + diff);
    return x;
};

/** Sonntag (00:00) der Woche, in der `d` liegt — ISO. */
const endOfIsoWeek = (d: Date): Date => {
    const x = startOfIsoWeek(d);
    x.setDate(x.getDate() + 6);
    return x;
};

/** Liegt das Datum in der aktuellen Kalenderwoche (Mo–So, inkl. heute/vergangen)? */
export const isThisWeek = (iso?: string | null): boolean => {
    const d = dayDate(iso);
    if (!d) return false;
    const now = new Date();
    return d.getTime() >= startOfIsoWeek(now).getTime() && d.getTime() <= endOfIsoWeek(now).getTime();
};

/** Frist = heute (inkl. überfällig wird separat über isOverdueFrist markiert). */
export const isDueToday = (iso?: string | null): boolean => isToday(iso);

/** TWV = heute. */
export const isPlannedToday = (iso?: string | null): boolean => isToday(iso);

/** Frist in dieser Woche (Mo–So) — inkl. heute und bereits vergangener Wochentage. */
export const isThisWeekDue = (iso?: string | null): boolean => isThisWeek(iso);

/** TWV in dieser Woche (Mo–So) — inkl. heute und bereits vergangener Wochentage. */
export const isThisWeekPlanned = (iso?: string | null): boolean => isThisWeek(iso);

/** Frist überschritten (offen, Frist < heute) → rot markieren („überfällig", D3). */
export const isOverdueFrist = (item: {
    frist?: string | null;
    status?: string;
}): boolean => item.status !== "Erledigt" && isPastDay(item.frist);

export const deDate = (iso?: string | null): string => {
    const d = dayDate(iso);
    return d ? d.toLocaleDateString("de-DE") : "—";
};

export const deTime = (iso: string): string =>
    toGraphDate(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

export const greeting = (): string => {
    const h = new Date().getHours();
    if (h < 11) return "Guten Morgen";
    if (h < 18) return "Guten Tag";
    return "Guten Abend";
};

export const todayEyebrow = (): string =>
    new Date().toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

/* ────────── Status-Farben ────────── */

export const statusColors = (status: string): { bg: string; fg: string } => {
    switch (status) {
        case "Erledigt": return { bg: REFINED.okBg, fg: REFINED.ok };
        // P5: Kontaktstatus-Chips in der Geburtstage-Karte.
        case "Kunde": return { bg: REFINED.okBg, fg: REFINED.ok };
        case "Interessent": return { bg: "#fdf3d6", fg: "#8a6d00" };
        default: return { bg: REFINED.aub50, fg: REFINED.aub };
    }
};

/* ────────── Ein-/Ausklappen (P1): persistenter Zustand + Chevron-Toggle ──────────
 * Generisch in DashCard + SectionGroup verdrahtet → alle Karten erben das
 * Verhalten ohne Call-Site-Änderung. Zustand pro Karte in localStorage
 * (Default: ausgeklappt). Key = Kartentitel (alle Titel sind eindeutig).
 * Hook und Knopf liegen geteilt in hooks/usePersistentCollapsed bzw.
 * components/common/CollapseToggle — die Speicherleiste der Kundenakte nutzt
 * dieselben Bausteine. */

const COLLAPSE_PREFIX = "kmvs.dash.collapsed.";

/* ────────── Karte ────────── */

export const DashCard: React.FC<{
    title: string;
    icon?: React.ReactNode;
    right?: React.ReactNode;
    /**
     * Steht unmittelbar RECHTS NEBEN der Überschrift (PO 14.09.2026) — für eine
     * Auswahl, die sagt, WAS die Karte gerade zeigt. Sie gehört zum Namen der
     * Liste, nicht zu den Filtern am rechten Rand.
     */
    afterTitle?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, icon, right, afterTitle, children }) => {
    // Auf dem Phone: schmaleres Innen-Padding + umbruchfreundlicher Kopf, damit
    // breite Inhalte (Chips, Pills, Listenzeilen) bei 375px nicht überlaufen.
    const { isPhone } = useResponsive();
    const [collapsed, toggleCollapsed] = usePersistentCollapsed(COLLAPSE_PREFIX + title);
    return (
        <section
            style={{
                backgroundColor: REFINED.white,
                border: `1px solid ${REFINED.line}`,
                borderRadius: 20,
                boxShadow: REFINED.cardShadow,
                overflow: "hidden",
            }}
        >
            <header
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: isPhone ? 10 : 16,
                    rowGap: 8,
                    flexWrap: "wrap",
                    padding: isPhone ? "16px 14px" : "20px 24px",
                    borderBottom: collapsed ? "none" : `1px solid ${REFINED.line}`,
                }}
            >
                <div style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 0, flexWrap: "wrap" }}>
                    {icon && <span style={{ color: REFINED.aub, display: "inline-flex", flexShrink: 0 }}>{icon}</span>}
                    <h2
                        style={{
                            margin: 0,
                            fontFamily: BRAND.fontAccent,
                            fontWeight: 600,
                            fontSize: isPhone ? "1.3rem" : "1.5rem",
                            color: REFINED.ink,
                            lineHeight: 1.1,
                            minWidth: 0,
                            overflowWrap: "anywhere",
                        }}
                    >
                        {title}
                    </h2>
                    {afterTitle}
                </div>
                <div
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                        rowGap: 8,
                        gap: isPhone ? 8 : 12,
                        minWidth: 0,
                    }}
                >
                    {right}
                    <CollapseToggle collapsed={collapsed} onToggle={toggleCollapsed} label={title} />
                </div>
            </header>
            {!collapsed && (
                <div style={{ padding: isPhone ? "6px 14px 16px" : "8px 24px 20px" }}>{children}</div>
            )}
        </section>
    );
};

/* ────────── Segmented-Control (Alle/Team/Eigene, Art der Vorgänge) ────────── */

export function Segmented<T extends string>({
    options,
    value,
    onChange,
    label,
}: {
    options: { key: T; label: string }[];
    value: T;
    onChange: (v: T) => void;
    /** Wofür diese Leiste steht — nötig, sobald zwei nebeneinander stehen. */
    label?: string;
}) {
    return (
        <div
            role="group"
            aria-label={label}
            style={{
                display: "inline-flex",
                gap: 2,
                padding: 3,
                borderRadius: 999,
                backgroundColor: REFINED.aub50,
                border: `1px solid ${REFINED.line}`,
            }}
        >
            {options.map((o) => {
                const on = o.key === value;
                return (
                    <button
                        key={o.key}
                        type="button"
                        aria-pressed={on}
                        onClick={() => onChange(o.key)}
                        style={{
                            border: "none",
                            cursor: "pointer",
                            borderRadius: 999,
                            padding: "6px 16px",
                            fontFamily: REFINED.fontUI,
                            fontSize: "0.85rem",
                            fontWeight: 700,
                            backgroundColor: on ? REFINED.aubDeep : "transparent",
                            color: on ? REFINED.white : REFINED.ink2,
                            transition: "background-color 140ms, color 140ms",
                        }}
                    >
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

/* ────────── Filter-Pill mit Count-Badge ────────── */

export const FilterPill: React.FC<{
    label: string;
    count: number;
    active: boolean;
    onClick: () => void;
}> = ({ label, count, active, onClick }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 8px 8px 16px",
            borderRadius: 999,
            cursor: "pointer",
            border: active ? "1px solid transparent" : `1px solid ${REFINED.line}`,
            backgroundColor: active ? REFINED.aubDeep : REFINED.white,
            color: active ? REFINED.white : REFINED.ink2,
            fontFamily: REFINED.fontUI,
            fontSize: "0.9rem",
            fontWeight: 700,
            whiteSpace: "nowrap",
            transition: "background-color 140ms, color 140ms, border-color 140ms",
        }}
    >
        {label}
        <span
            style={{
                minWidth: 22,
                height: 22,
                padding: "0 7px",
                borderRadius: 999,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.78rem",
                fontWeight: 800,
                backgroundColor: active ? "rgba(255,255,255,0.22)" : REFINED.aub50,
                color: active ? REFINED.white : REFINED.ink2,
            }}
        >
            {count}
        </span>
    </button>
);

/* ────────── Initialen-Avatar ────────── */

export const InitialsAvatar: React.FC<{ name: string; size?: number }> = ({ name, size = 28 }) => (
    <Avatar name={name} color="colorful" size={size <= 24 ? 24 : 28} />
);

/* ────────── Status-Chip ────────── */

export const StatusChip: React.FC<{ status: string }> = ({ status }) => {
    const c = statusColors(status);
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 12px",
                borderRadius: 999,
                backgroundColor: c.bg,
                color: c.fg,
                fontSize: "0.8rem",
                fontWeight: 700,
                whiteSpace: "nowrap",
            }}
        >
            <span style={{ width: 7, height: 7, borderRadius: 999, backgroundColor: c.fg }} />
            {status}
        </span>
    );
};

/* ────────── Outlook-Herkunfts-Badge (Dashboard-Termine-Review SP2) ──────────
 * Kennzeichnet Geburtstage, die NICHT aus einem KMVS-Kontakt stammen, sondern
 * aus dem persönlichen Outlook-Kalender geladen wurden (Entscheidung 5: rein
 * informativ). Stil analog StatusChip (Pille, kvp-rd-Tokens),
 * dezent aub-getönt mit Kalender-Icon. */
export const OutlookBadge: React.FC = () => (
    <span
        style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "3px 10px",
            borderRadius: 999,
            // aub sehr dezent hinterlegt (8 %-Alpha am Hex) — abgesetzt vom
            // kräftigen StatusChip, damit die Outlook-Zeilen sekundär wirken.
            backgroundColor: `${REFINED.aub}14`,
            color: REFINED.aub,
            fontSize: "0.72rem",
            fontWeight: 800,
            whiteSpace: "nowrap",
            letterSpacing: "0.01em",
        }}
        title="Geburtstag aus dem Outlook-Kalender"
    >
        <CalendarLtr16Regular style={{ fontSize: 13 }} />
        Outlook
    </span>
);

export const EmptyHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div
        style={{
            padding: "28px 12px",
            textAlign: "center",
            color: REFINED.ink3,
            fontFamily: REFINED.fontUI,
            fontSize: "0.92rem",
            fontStyle: "italic",
        }}
    >
        {children}
    </div>
);

/* ────────── „Mehr laden"-Button (P2/P5: client-seitiges Fenstern) ──────────
 * Geteilter, unauffälliger Voll-Breite-Button unter einer beschnittenen Liste;
 * `rest` = noch nicht gezeigte Einträge. */
export const MehrLadenButton: React.FC<{ rest: number; onClick: () => void; label?: string }> = ({
    rest,
    onClick,
    label = "Mehr laden",
}) => (
    <div style={{ padding: "10px 6px 4px" }}>
        <button
            type="button"
            onClick={onClick}
            style={{
                width: "100%",
                minHeight: 40,
                borderRadius: 12,
                border: `1px solid ${REFINED.line}`,
                background: REFINED.white,
                color: REFINED.aub,
                cursor: "pointer",
                fontFamily: REFINED.fontUI,
                fontSize: "0.88rem",
                fontWeight: 700,
            }}
        >
            {label} ({rest})
        </button>
    </div>
);

/* ────────── Layout-Bausteine (Block 1 „Meine Arbeit" + Block-2-Raster) ──────────
 * SectionGroup ist eine äußere Rahmen-Karte (Optik wie DashCard) mit Serifen-
 * Überschrift, die ihre Kinder als volle-Breite-Unterkarten vertikal stapelt.
 * DashGrid ist das 2-Spalten-Raster für Block 2 — auf schmalen Screens 1-spaltig. */

/** Rahmen-Karte mit Titel (z. B. „Meine Arbeit"), die Kinder gestapelt enthält. */
export const SectionGroup: React.FC<{
    title: string;
    icon?: React.ReactNode;
    right?: React.ReactNode;
    children: React.ReactNode;
}> = ({ title, icon, right, children }) => {
    // Phone: schmaleres Padding + umbruchfreundlicher Kopf, damit der „Meine
    // Arbeit"-Rahmen und seine gestapelten Unterkarten bei 375px nicht überlaufen.
    const { isPhone } = useResponsive();
    const [collapsed, toggleCollapsed] = usePersistentCollapsed(COLLAPSE_PREFIX + title);
    return (
        <section
            style={{
                backgroundColor: REFINED.white,
                border: `1px solid ${REFINED.line}`,
                borderRadius: 20,
                boxShadow: REFINED.cardShadow,
                overflow: "hidden",
            }}
        >
            <header
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: isPhone ? 10 : 16,
                    rowGap: 8,
                    flexWrap: "wrap",
                    padding: isPhone ? "16px 14px" : "20px 24px",
                    borderBottom: collapsed ? "none" : `1px solid ${REFINED.line}`,
                }}
            >
                <div style={{ display: "inline-flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    {icon && <span style={{ color: REFINED.aub, display: "inline-flex", flexShrink: 0 }}>{icon}</span>}
                    <h2
                        style={{
                            margin: 0,
                            fontFamily: BRAND.fontAccent,
                            fontWeight: 600,
                            fontSize: isPhone ? "1.3rem" : "1.5rem",
                            color: REFINED.ink,
                            lineHeight: 1.1,
                            minWidth: 0,
                            overflowWrap: "anywhere",
                        }}
                    >
                        {title}
                    </h2>
                </div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: isPhone ? 8 : 12, minWidth: 0 }}>
                    {right}
                    <CollapseToggle collapsed={collapsed} onToggle={toggleCollapsed} label={title} />
                </div>
            </header>
            {!collapsed && (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: isPhone ? 12 : 16,
                        padding: isPhone ? "12px 10px 14px" : "16px 16px 18px",
                    }}
                >
                    {children}
                </div>
            )}
        </section>
    );
};

/** 2-Spalten-Raster (Desktop/Tablet) — auf Phones (≤768px) 1-spaltig. */
export const DashGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isPhone } = useResponsive();
    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr",
                gap: isPhone ? 16 : 24,
                alignItems: "start",
            }}
        >
            {children}
        </div>
    );
};
